<template>
  <circle-loader v-if="loading || gettingData" />
  <template v-else>
    <div
      class="loan-options-wrapper"
      :class="{ dark, mobile: isMobile, tablet: isTablet }"
      v-bind="$attrs"
    >
      <pel-loan-options-header
				v-show="rateTypeActive > 0"
        :rateTypeActive="rateTypeActive"
        :isMobile="isMobile"
        :ratePlanCount="ratePlanCount"
        @setCurrentRateType="setCurrentRateType"
      />
      <div
        class="loan-options-navigation-container loan-options-buttons-group"
        v-show="availableTerms.length > 0 && ratePlanCount > 1"
      >
        <button
          v-for="(term, index) in availableTerms"
          :key="term"
          :class="{ active: pagination.currentRepaymentTerm === term }"
          @click.prevent="changePage(Number(index) + 1)"
        >
          {{ term }} Year
        </button>
      </div>
      <div class="loan-options-container-wrapper">
        <div v-if="ratePlanCount > 1" class="loan-options-container-left" @click="goBack">
          <img src="@/assets/images/chevron-left.png" alt="left arrow icon" />
        </div>
        <div class="loan-options-container">
          <div class="loan-options application-steps" :data-count="carouselData.length">
            <div
              v-for="(item, index) in carouselData"
              :key="item.id"
              class="loan-option"
              :position="setPosition(index + 1)"
            >
              <pel-loan-option
                v-if="setPosition(index + 1) != ''"
                :item="item"
                :position="setPosition(index + 1)"
                @goBack="goBack"
                @goNext="goNext"
              />
            </div>
          </div>
        </div>
        <div v-if="ratePlanCount > 1" class="loan-options-container-right" @click="goNext">
          <img src="@/assets/images/chevron-right.png" alt="right arrow icon" />
        </div>
      </div>
      <div class="loan-options-navigation-container pagination" v-if="ratePlanCount > 1">
        <div class="slider">
          <a
            v-for="page in pagination.pages"
            :key="page"
            :class="{ active: page === pagination?.currentPage }"
          ></a>
        </div>
      </div>
    </div>
    <div v-if="error" class="loan-options-error-wrapper">
      <p v-if="errorMessages.length <= 0">
        Something has gone wrong submitting your request. If you continue to receive this error,
        please contact support.
      </p>
      <ul v-else>
        <li v-for="(error, index) of errorMessages" :key="index">
          {{ error }}
        </li>
      </ul>
    </div>
    <div class="loan-options-navigation-footer" :data-count="carouselData.length">
      <next-button
        v-if="showPreviousButton"
        :disabled="loading || gettingData"
        text="Previous"
        class="cancel-btn"
        @click="$emit('previous', true)"
      ></next-button>
      <next-button
        @click="saveChanges"
        text="Save Changes"
        class="next-btn sc-next-button"
        :disabled="saveChangesDisabled || loading || gettingData || savingChanges"
        :loading="savingChanges"
      />
    </div>
  </template>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { useStore } from "vuex";
import PelLoanOptionsHeader from "@/components/pel-loan-configuration/PelLoanOptionsHeader.vue";
import PelLoanOption from "@/components/pel-loan-configuration/PelLoanOption.vue";
import NextButton from "@/components/shared/NextButton.vue";
import useHtmlUtilities from "@/use/htmlUtilities";
import { RateTypeIds } from "@/config/constants";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";

interface Pagination {
  currentPage: number;
  perPage: number;
  totalPages: number;
  pages: number[];
  currentRepaymentTerm: number;
  variableRateCurrentPage: number;
  fixedRateCurrentPage: number;
}

const store = useStore();

const { isColorDark } = useHtmlUtilities();

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  isCurrent: {
    type: Boolean,
    required: false,
  },
  showPreviousButton: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (e: "complete", payload: boolean): void;
  (e: "previous", payload: boolean): void;
  (e: "close", payload: boolean): void;
}>();

const gettingData = ref<boolean>(true);
const savingChanges = ref<boolean>(false);
const carouselData = ref([] as any[]);
const windowWidth = ref<number>(0);

const { editingFromConfirmYourLoanDetails } = useApplicationInfo();

const isMobile = computed(() => windowWidth.value <= 576);

const isTablet = computed(() => windowWidth.value <= 992 && windowWidth.value > 576);

const dark = computed(() => isColorDark(store.state.lenderData.primaryColor));

const loanConfigurationModule = computed(() => store.state.loanConfigurationModule);

const error = computed<boolean>(() => store.state.loanConfigurationModule.error);

const errorMessages = computed<Array<string>>(
  () => store.state.loanConfigurationModule.errorMessages,
);

const pelLoanOptionsState = computed(
  () => store.state.loanConfigurationModule.loanOptionsConfiguration,
);

const rateTypeSections = computed(
  () => store.state.loanConfigurationModule.loanOptionsConfiguration.rateTypeSections,
);

const rateTypeActive = ref<number>(0);

const availableTerms = computed(() => {
  if (currentlyViewing.value?.length > 0) {
    return (
      currentlyViewing.value
        .map((rp: any) => rp.repaymentTerm)
        .sort((a: number, b: number) => a - b) || []
    );
  }
  return [];
});

const saveChangesDisabled = computed<boolean>(
  () => !selectedRatePlan.value?.id && !selectedRepaymentOption.value?.repaymentTypeId,
);

const fixedRatePlans = computed(() => store.state.loanConfigurationModule.fixedRatePlans);

const variableRatePlans = computed(() => store.state.loanConfigurationModule.variableRatePlans);

const previouslySelectedRatePlan = computed(
  () => store.state.loanConfigurationModule.loanOptionsConfiguration.selectedRatePlan,
);

const selectedRatePlan = computed(() => store.state.loanConfigurationModule.selectedRatePlan);

const selectedRepaymentOption = computed(
  () => store.state.loanConfigurationModule.selectedRepaymentOption,
);

const previouslySelectedRepaymentOption = computed(
  () => store.state.loanConfigurationModule.loanOptionsConfiguration.selectedRepaymentOption,
);

const validRatePlans = computed<boolean>(() => carouselData.value && carouselData.value.length > 0);

const ratePlanCount = computed<number>(
  () => (carouselData.value && carouselData.value.length) ?? 0,
);

const pagination = reactive({
  currentPage: 1,
  variableRateCurrentPage: 1,
  fixedRateCurrentPage: 1,
  perPage: 1,
  totalPages: computed<number>(() =>
    Math.ceil((carouselData.value || []).length / pagination.perPage),
  ),
  pages: computed<Array<number>>(() => [...Array(pagination.totalPages).keys()].map(i => i + 1)),
  currentRepaymentTerm: computed<number>(() => carouselData.value[0]?.repaymentTerm ?? 0),
}) as Pagination;

const currentlyViewing = computed(() => {
  if (rateTypeActive.value === 1) {
    return fixedRatePlans.value;
  } else if (rateTypeActive.value === 2) {
    return variableRatePlans.value;
  } else {
    return [];
  }
});

function setPosition(dataIndex: number) {
  if (dataIndex === carouselData.value?.length && carouselData.value?.length > 1) {
    return "left";
  } else if (dataIndex === 1) {
    return "center";
  } else if (dataIndex === 2) {
    return "right";
  } else {
    return "";
  }
}

function handlePaginationState() {
  if (rateTypeActive.value === RateTypeIds.FIXED) {
    pagination.fixedRateCurrentPage = pagination.currentPage;
  }

  if (rateTypeActive.value === RateTypeIds.VARIABLE) {
    pagination.variableRateCurrentPage = pagination.currentPage;
  }
}

async function setCurrentRateType(rateTypeId: number) {
  rateTypeActive.value = rateTypeId;

  if (rateTypeId === RateTypeIds.FIXED) {
    carouselData.value = fixedRatePlans.value;
  } else if (rateTypeId === RateTypeIds.VARIABLE) {
    carouselData.value = variableRatePlans.value;
  } else {
    carouselData.value = [];
  }
}

async function changePage(pageNumber: number) {
  const spliceSize = pageNumber - pagination.currentPage;
  const iterations = [...Array(Math.abs(spliceSize)).keys()].map(i => i + 1);

  if (pageNumber > pagination.currentPage) {
    iterations.forEach(() => {
      goNext();
    });
  }

  if (pageNumber < pagination.currentPage) {
    iterations.forEach(() => {
      goBack();
    });
  }
}

async function goNext() {
  if (pagination.currentPage >= pagination.totalPages) {
    // set current page to first item
    pagination.currentPage = 1;
  } else {
    pagination.currentPage++;
  }
  handlePaginationState();
  next();
}

async function goBack() {
  if (pagination.currentPage === 1) {
    // set current page to last item
    pagination.currentPage = pagination.pages[pagination.pages.length - 1];
  } else {
    pagination.currentPage--;
  }
  handlePaginationState();
  previous();
}

function previous() {
  if (validRatePlans.value) {
    // Update order of items in data array to be shown in carousel
    carouselData.value.unshift(carouselData.value.pop());
  }
}

function next() {
  if (validRatePlans.value) {
    // Update order of items in data array to be shown in carousel
    carouselData.value.push(carouselData.value.shift());
  }
}

async function saveChanges() {
  savingChanges.value = true;
  store.dispatch("loanConfigurationModule/setSavingChanges", true);

  if (!saveChangesDisabled.value) {
    await store.dispatch(
      "loanConfigurationModule/updateLoanConfiguration",
      editingFromConfirmYourLoanDetails.value,
    );
    emit("complete", true);
  } else {
    savingChanges.value = false;
    store.dispatch("loanConfigurationModule/setSavingChanges", false);
  }
}

async function getPreviouslySelectedRatePlan() {
  if (previouslySelectedRatePlan.value) {
    store.dispatch("loanConfigurationModule/setSelectedRatePlan", previouslySelectedRatePlan.value);

    await setCurrentRateType(previouslySelectedRatePlan.value.rateTypeId);

    let i = carouselData.value.findIndex(
      (rp: any) => rp.id === previouslySelectedRatePlan.value.id,
    );

    if (i > -1) {
      changePage(i + 1);
    }
    if (previouslySelectedRepaymentOption.value) {
      store.dispatch(
        "loanConfigurationModule/setSelectedRepaymentOption",
        previouslySelectedRepaymentOption.value,
      );
    }
  } else {
    setDefaultRateSection();
  }
}

function setDefaultRateSection() {
  if (rateTypeSections.value.length === 1) {
    rateTypeActive.value = rateTypeSections.value[0].rateTypeId;
    setCurrentRateType(rateTypeActive.value);
  } else {
    rateTypeActive.value = RateTypeIds.FIXED;
    carouselData.value = fixedRatePlans.value;
  }
}
function handleResize() {
  windowWidth.value = window.innerWidth;
}

async function getPelLoanOptions() {
  gettingData.value = true;

  await store.dispatch("loanConfigurationModule/getAvailableRatePlans");

  if (errorMessages.value?.length === 0) {
    await getPreviouslySelectedRatePlan();
  }

  windowWidth.value = window.innerWidth;

  gettingData.value = false;
}

onMounted(async () => {
  savingChanges.value = false;
  store.dispatch("loanConfigurationModule/setSavingChanges", false);

  window.addEventListener("resize", handleResize);
  await getPelLoanOptions();

  gettingData.value = false;
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

watch(
  () => rateTypeActive.value,
  (newValue, oldValue) => {
    if (oldValue > 0 && newValue > 0) {
      if (newValue === RateTypeIds.FIXED) {
        pagination.currentPage = pagination.fixedRateCurrentPage;
      }

      if (newValue === RateTypeIds.VARIABLE) {
        pagination.currentPage = pagination.variableRateCurrentPage;
      }
    }
  },
);
</script>

<style scoped>
.loan-options-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.loan-options-container-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: 100%;
  width: 100%;
  min-height: 350px;
}

.loan-options-container-left {
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: #ffffff;
  box-shadow: 12px 10px 20px #ffffff;
  min-width: 7.5vw;
}

.loan-options-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  width: 100%;

  --loan-options-container-width: 100%;
}

.loan-options-container-right {
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: #ffffff;
  box-shadow: -12px 10px 20px #ffffff;
  width: 7.5vw;
}

.loan-options-navigation-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loan-options-buttons-group {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.loan-options-buttons-group button {
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e2e3e8;
  min-height: 40px;
  min-width: 89px;
}

.loan-options-buttons-group button.active {
  background-color: #f2f3f5;
  font-weight: var(--font-weight-bold);
}

.loan-options {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.loan-option {
  background: #f2f3f5;
  max-width: var(--loan-options-container-width);
  opacity: 0;
  visibility: hidden;
  display: none;
  width: 0%;
  border-radius: 6px;
}

.loan-option[position="center"] {
  visibility: visible;
  display: block;
  opacity: 1;
  width: 100%;
}

.pagination {
  padding-top: 1rem;
}
.pagination .slider > a {
  display: inline-flex;
  width: 0.75rem;
  height: 0.75rem;
  background: white;
  border: 1px solid var(--theme-color);
  text-decoration: none;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 0.25rem 0.5rem 0;
  position: relative;
}

.pagination .slides > div {
  scroll-snap-align: start;
  flex-shrink: 0;
  width: 300px;
  height: 300px;
  margin-right: 50px;
  border-radius: 10px;
  background: #eee;
  transform-origin: center center;
  transform: scale(1);
  transition: transform 0.5s;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 100px;
}

.pagination .slider a.active {
  background-color: var(--theme-color, #000);
}

.pagination .slider > a:active {
  top: 1px;
  background-color: var(--theme-color, #000);
}

.loan-options-navigation-footer {
  margin-bottom: 1rem;
}
.loan-options-navigation-footer button {
  height: 34px;
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 110px;
}

.loan-options-navigation-footer:is([data-count="1"], [data-count="2"]) {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: 1rem;
  gap: 1rem;
}

.loan-options-navigation-footer:not([data-count="1"], [data-count="2"]) {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  width: 95vw;
  gap: 1rem;
}

.cancel-btn {
  background: #f2f3f5;
  color: #000000;
  border: solid 1px #f2f3f5;
  cursor: pointer;
}

.next-btn {
  cursor: pointer;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
  border: none;
  border-color: transparent;
}

.btn:not(.next-btn):disabled:hover {
  background-color: transparent;
  border: none;
  border-color: transparent;
}

.loan-options-error-wrapper {
  text-align: center;
}
.loan-options-error-wrapper ul {
  color: #bb0000;
  font-weight: var(--font-weight-bold);
}
.loan-options-error-wrapper ul li {
  list-style: none;
}

@media (min-width: 992px) {
  .loan-options-navigation-footer:not([data-count="1"], [data-count="2"]) {
    width: calc(95vw - 7.5vw);
  }

  .loan-options-navigation-container.loan-options-buttons-group {
    display: none;
  }

  .loan-options-container-right {
    max-width: 7.5vw;
    overflow: hidden;
  }

  .loan-option {
    z-index: 0;
    padding: 2rem;
  }
  .loan-options-container {
    width: 100%;
  }

  .loan-options-container {
    max-width: 82vw;
  }
  .loan-options {
    gap: 1rem;
  }

  .application-steps {
    height: 100%;
    transition: bottom 1s ease;
    width: max-content;
    overflow-x: hidden;
    overflow-y: visible;
  }

  .loan-options:is([data-count="1"]) .loan-option[position="center"] {
    transform: translateX(0%);
  }

  .loan-option[position="left"] {
    visibility: visible;
    display: block;
    opacity: 1;
    min-width: 60vw;
    transform: translateX(-290%);
  }

  .loan-option[position="center"] {
    z-index: 1;
    min-width: 60vw;
    visibility: visible;
    display: block;
    opacity: 1;
    transform: translateX(22%);
  }

  .loan-option[position="right"] {
    visibility: visible;
    display: block;
    opacity: 1;
    min-width: 60vw;
    transform: translateX(25%);
  }
}

@media (max-width: 992px) {
  .loan-option[position="center"] {
    padding: 2rem;
  }

  .loan-options-navigation-footer:is([data-count="1"], [data-count="2"]) {
    margin: 1rem 0 0 0;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .loan-options-navigation-footer:is([data-count="2"]) {
    margin-top: 24px;
  }

  .loan-options-navigation-footer:not([data-count="1"], [data-count="2"]) {
    justify-content: center;
    margin-top: 1rem;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .loan-options-container-right img,
  .loan-options-container-left img {
    width: 14px;
    height: 23px;
  }

  .pagination {
    visibility: hidden;
    display: none;
    opacity: 0;
  }

  .loan-options-buttons-group {
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1em;
    margin: 0 1rem 1rem;
  }

  .loan-option,
  .loan-option[position="center"] {
    padding: 1rem;
  }
}
</style>
